/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { TicketTextFontPath } from './TicketTextFontPath';
import { TicketContainer, TICKET_CHANGE_EVENTS } from '../tickets/TicketContainer';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { TICKET_ACTIVITY_NAME_SETTINGS } from './TICKET_TEXT_SETTINGS';
import { Side, Ticket } from '@touchplan/common';
import { TicketUtils } from '@appCanvasCommon/utils/TicketUtils';
import { LevelOfDetail, LEVEL_OF_DETAIL_SIGNALS } from '@appPlanApplication/lod/LevelOfDetail';
import {
	TextComponents,
	SpecialLODConditions,
	getLodDefinition,
} from '@appPlanApplication/lod/LevelOfDetailDefinition';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { TicketType } from '@touchplan/common';
import { IFontOptions } from '@appCanvasCommon/fontpath/fontpath.interfaces';
import { WORD_WRAP_MODE } from '@appCanvasCommon/fontpath/WordWrapper';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { LayerPaths } from '@appPlanApplication/LayerPaths';

export class TicketActivityNameFontPath extends TicketTextFontPath {
	// TODO: Apply the mask globally on the world
	public maskContainer: FancyContainer = new FancyContainer();
	private _unsubUpdate: any;
	private _lastWidth: number;

	constructor(
		fontFamily: string,
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer,
		text: string = '',
		fontOptions: IFontOptions = {
			fontSize: 32,
			wordWrapOptions: { mode: WORD_WRAP_MODE.GREEDY, lineHeight: '32px' },
		}
	) {
		super(fontFamily, canvasAngularLand, ticketContainer, text, fontOptions);

		this.maskContainer.addChild(this);
		this.clipToMaskArea = true;

		this.eventMode = EventMode.None;

		ticketContainer.on(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		LevelOfDetail.emitter.on(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.lodChanged);
		// this._unsubUpdate = ticketContainer.camera.changed$.subscribe(() => {this.update();});

		this.onChanged(ticketContainer);
		this.lodChanged();
	}

	public update = (
		canvasAngularLand: CanvasAngularLand,
		dataContainer: TicketContainer,
		forceDirty: boolean = false
	) => {
		if (!this.ticket.visible || !this.ticket.renderable) {
			return;
		}
		// when the ticket width changes due is granular, we need to recalculate the text.
		const layout = dataContainer.getLayout();
		if (this._lastWidth !== layout.width && layout.currentLayer === LayerPaths.UI_GRANULAR_TICKETS) {
			this.dirty = true;
		}
		// As part of TP-7363, we noticed that this function is marking the component as dirty every time as a side effect,
		// which causes the check below to be ineffective. For now, we will remove it to see if this improves performance.
		// this._shift(this.ticket.getData());
		if (!this.dirty && !forceDirty) {
			return;
		}
		this.onChanged(this.ticket, forceDirty);
	};

	public override destroy(options?): void {
		this.ticket.off(TICKET_CHANGE_EVENTS.CHANGED, this.onChanged);
		LevelOfDetail.emitter.off(LEVEL_OF_DETAIL_SIGNALS.CHANGED, this.lodChanged);
		super.destroy(options);
	}

	private _getFontSize(fontData): number {
		return fontData.fontSize * this._getTextScale();
	}

	private _getScaledLineHeight(fontData): string {
		return Math.round(fontData.fontSize * this._getTextScale()) + 'px';
	}

	private _getTextScale(): number {
		return this.canvasAngularLand.userPreferenceStateService.canvasTextScale || 1;
	}

	/**
	 * Aligns the ticket activity name for both the active and pull sides
	 * From spec:
	 * - ACTIVE:
	 * -- fontSize 32px if durationDays === 1
	 * -- fontSize: 42px if durationDays > 1
	 * -- font-weight: 500
	 * -- line-height: 32px if duration days === 1
	 * -- line-height is 36px if durationDays > 1
	 * -- Multi-line Text align left
	 * - PULL:
	 * -- fontSize: 32px
	 * -- lineHeight: 36px
	 * -- fontWeight: 500
	 * -- Multi-line Text Align center
	 * -- Align text to center of container.
	 * @param {Ticket} data The ticket model
	 */
	private _alignTask(data: Ticket): void {
		//need current lod
		const side: Side = data.view.side;
		const currentLod = LevelOfDetail.getLOD();
		const oneDayArr = data.view.liveDurationDays > 1 ? [] : [SpecialLODConditions.isOneDayTicket];

		//this needs to not run on frequent updates
		const lodArr: any = [
			TicketType.Task,
			TextComponents.activityNameComponent,
			side,
			...oneDayArr,
			currentLod,
		];

		const fontData = getLodDefinition(lodArr);

		this.fontSize = this._getFontSize(fontData);
		this.lineHeight = this._getScaledLineHeight(fontData);

		if (side === Side.ACTIVE) {
			this.align.x = false;
			this.align.y = false;
			this.alignment = 'left';
		} else if (side === Side.PULL) {
			this.align.y = true;
			this.alignment = 'center';
		}
	}
	/**
	 * Align the ticket activity name for milestones on both the active and pull side
	 * From Spec:
	 * - ACTIVE:
	 * -- fontSize: 32px
	 * -- fontWeight: 500
	 * -- lineHeight: 32px
	 * -- Multi-line Text aligned center
	 * - PULL:
	 * -- fontSize: 36px
	 * -- fontWeight: 500
	 * -- lineHeight: 36px
	 * -- Multi-line Text aligned center
	 * -- Align to center of container
	 * @param {Ticket} data
	 */
	private _alignMilestone(data: Ticket): void {
		const side: Side = data.view.side;

		const currentLod = LevelOfDetail.getLOD();
		const fontData = getLodDefinition([
			TicketType.Milestone,
			TextComponents.activityNameComponent,
			side,
			currentLod,
		] as any);
		this.fontSize = this._getFontSize(fontData);
		this.lineHeight = this._getScaledLineHeight(fontData);

		if (side === Side.ACTIVE) {
			this.align.x = false;
			this.align.y = true;
			this.alignment = 'center';
		} else if (side === Side.PULL) {
			this.align.y = true;
			//this.align.x = true;
			this.alignment = 'center';
		}
	}
	/**
	 * This aligns the constraint ticket description text for both the active and pull sides
	 * From spec:
	 * - ACTIVE:
	 * -- fontSize: 32px
	 * -- fontWeight: 500
	 * -- lineHeight: 32px
	 * -- Multi-line Text aligned left
	 * - PULL:
	 * -- fontSize: 32px
	 * -- fontWeight: 500
	 * -- lineHeight: 36;
	 * -- Multi-line Text aligned center
	 * -- Align to center of container.
	 * @param {Ticket} data
	 */
	private _alignConstraint(data: Ticket): void {
		const side: Side = data.view.side;

		const currentLod = LevelOfDetail.getLOD();
		const fontData = getLodDefinition([
			TicketType.Constraint,
			TextComponents.activityNameComponent,
			side,
			currentLod,
		] as any);

		this.fontSize = this._getFontSize(fontData);
		this.lineHeight = this._getScaledLineHeight(fontData);

		if (side === Side.ACTIVE) {
			this.align.x = false;
			this.align.y = true;
			this.alignment = 'center';
			// this.fontSize = 32;
			// this.lineHeight = "32px";
		} else if (side === Side.PULL) {
			this.align.y = true;
			this.alignment = 'center';
			// this.fontSize = 32;
			// this.lineHeight = "36px";
		}
	}

	private _handleSide = () => {
		const data = this.ticket.getData();
		const type = TicketUtils.getTicketType(data);

		// We could simplify this A LOT we have common elements...but MOCA...Might be best to keep the alignment rules separate for the inevitable changes.
		// Simplify once things stabilize a bit...
		switch (type) {
			case TicketType.Constraint:
				this._alignConstraint(data);
				break;
			case TicketType.Milestone:
				this._alignMilestone(data);
				break;
			default:
				this._alignTask(data);
				break;
		}
		//this._shift(data);
	};

	private _shift(data: Ticket): void {
		const lastMask = this.maskArea.clone();
		this.applyOffsetAndDrawArea(this.ticket, TICKET_ACTIVITY_NAME_SETTINGS);
		const offset: number = this._getHeightOffset(data);
		this.maskArea.y += offset;
		this.maskArea.height -= offset;
		this.x = this.maskArea.x;
		this.y = this.maskArea.y;
		this.positionCenter();
		// Since fancy text now clips the mesh based on mask area and position, set dirty.
		if (!this.dirty) {
			this.dirty = !this.compareRects(lastMask, this.maskArea);
		}
	}
	public lodChanged = (
		newLod: number = LevelOfDetail.getLODLevel(),
		oldLod: number = LevelOfDetail.getLastLODLevel()
	) => {
		const data = this.ticket.getData();

		const type = TicketUtils.getTicketType(data);
		switch (type) {
			case TicketType.Task:
				this._alignTask(data);
				break;
			case TicketType.Milestone:
				this._alignMilestone(data);
				break;
			case TicketType.Constraint:
				this._alignConstraint(data);
				break;
			default:
				break;
		}
		if (this.dirty && this.ticket.visible && this.ticket.renderable) {
			this.calculateTextAsync();
		}
		return true;
	};

	private onChanged = (ticketContainer: TicketContainer, skipCheck: boolean = false) => {
		if (!ticketContainer.renderable || !ticketContainer.visible) {
			return;
		}
		this.tint = this.ticket.liveRoleTextColor;

		const data = ticketContainer.getData();
		const layout = ticketContainer.getLayout();

		this._shift(data);

		this._handleSide();

		this.setText(data.view.shortActivityName);
		// Update word wrap!
		if (layout.width !== this._lastWidth && !skipCheck) {
			this.dirty = true;
		}

		this.positionCenter();
		this.calculateText();

		// MAKE LESS VISIBLE
		if (data.view.isActualized) {
			this.alpha = 0.5;
		} else {
			this.alpha = 1.0;
		}

		this._lastWidth = layout.width;
	};
}
